import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Modal, Result, Steps } from "antd";
import { useTranslation } from "react-i18next";
import Spinner from "../../components/_shared/loader/Spinner";
import CoachStepTwo from "../../components/coaches-program/CoachStepTwo";
import CoachStepThree from "../../components/coaches-program/CoachStepThree";
import CoachStepFour from "../../components/coaches-program/CoachStepFour";
import CoachesImage from "../../assets/images/coaches.jpg";
import coachesStyles from "../coaches/Coaches.module.scss";
import coachFormStyles from "../../components/coaches-program/CoachForm.module.scss";
import styles from "./CoachApplicationReview.module.scss";
import {
  ApiErrorBody,
  CoachApplicationReviewRequest,
  CoachApplicationReviewResponse,
  CoachPersonalInfo,
  CoachProfessionalInfo,
} from "../../types/coachApplication";

const { Step } = Steps;

type PageState = "loading" | "invalid" | "form" | "success";

const toPersonalInfo = (data: CoachApplicationReviewResponse): CoachPersonalInfo => ({
  name: data.name,
  fathersName: data.fathersName,
  surname: data.surname,
  placeOfBirth: data.placeOfBirth,
  residence: data.residence,
  dateOfBirth: data.dateOfBirth,
  gender: data.gender,
  nationality: data.nationality,
  personalNumber: data.personalNumber,
  address: data.address,
});

const toProfessionalInfo = (data: CoachApplicationReviewResponse): CoachProfessionalInfo => ({
  clothingSize: data.clothingSize,
  languageSkills: data.languageSkills,
  careerAsPlayer: data.careerAsPlayer,
  careerAsCoach: data.careerAsCoach,
  otherExperience: data.otherExperience,
  phoneNumber: data.phoneNumber,
  email: data.email,
});

const toApplicationPayload = (
  courseId: string,
  personal: CoachPersonalInfo,
  professional: CoachProfessionalInfo
): CoachApplicationReviewRequest => ({
  courseId,
  name: personal.name,
  fathersName: personal.fathersName,
  surname: personal.surname,
  placeOfBirth: personal.placeOfBirth,
  residence: personal.residence,
  dateOfBirth: personal.dateOfBirth,
  gender: personal.gender,
  clothingSize: professional.clothingSize,
  nationality: personal.nationality,
  personalNumber: personal.personalNumber,
  address: personal.address,
  careerAsPlayer: professional.careerAsPlayer,
  careerAsCoach: professional.careerAsCoach,
  otherExperience: professional.otherExperience,
  languageSkills: professional.languageSkills,
  phoneNumber: professional.phoneNumber,
  email: professional.email,
});

const CoachApplicationReview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  // Emails send /coach-application-review?token=...; /coaches/review/:token is also supported.
  const token = pathToken || searchParams.get("token") || "";

  const [pageState, setPageState] = useState<PageState>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("");
  const [application, setApplication] = useState<CoachApplicationReviewResponse | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [personalInfo, setPersonalInfo] = useState<CoachPersonalInfo | undefined>();
  const [professionalInfo, setProfessionalInfo] = useState<CoachProfessionalInfo | undefined>();
  const [noticesOpen, setNoticesOpen] = useState(true);

  useEffect(() => {
    const fetchApplication = async () => {
      if (!token) {
        setErrorTitle(
          t("app.coachApplicationReview.invalidTitle", "Linku nuk është i vlefshëm")
        );
        setErrorMessage(
          t(
            "app.coachApplicationReview.missingToken",
            "Ky link nuk përmban një identifikues të vlefshëm."
          )
        );
        setPageState("invalid");
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/coach-applications/review/${encodeURIComponent(token)}`
        );

        if (!response.ok) {
          let msg = "";
          try {
            const body: ApiErrorBody = await response.json();
            if (body?.message) msg = body.message;
          } catch {
            // ignore JSON parse errors and fall back to status-based messages
          }

          if (!msg) {
            if (response.status === 401 || response.status === 403) {
              msg = t(
                "app.coachApplicationReview.unauthorized",
                "Nuk keni të drejtë ta hapni këtë aplikim."
              );
            } else if (response.status === 404) {
              msg = t(
                "app.coachApplicationReview.notFound",
                "Aplikimi nuk u gjet."
              );
            } else if (response.status === 400) {
              msg = t(
                "app.coachApplicationReview.invalidLink",
                "Ky link për rishikim nuk është më i vlefshëm ose ka skaduar."
              );
            } else {
              msg = t(
                "app.coachApplicationReview.networkError",
                "Ndodhi një gabim. Ju lutem provoni përsëri më vonë."
              );
            }
          }

          setErrorTitle(
            response.status === 401 || response.status === 403
              ? t("app.coachApplicationReview.unauthorizedTitle", "Qasja u refuzua")
              : t("app.coachApplicationReview.invalidTitle", "Linku nuk është i vlefshëm")
          );
          setErrorMessage(msg);
          setPageState("invalid");
          return;
        }

        const data: CoachApplicationReviewResponse = await response.json();
        setApplication(data);
        setPersonalInfo(toPersonalInfo(data));
        setProfessionalInfo(toProfessionalInfo(data));
        setCurrentStep(1);
        setPageState("form");
      } catch {
        setErrorTitle(
          t("app.coachApplicationReview.invalidTitle", "Linku nuk është i vlefshëm")
        );
        setErrorMessage(
          t(
            "app.coachApplicationReview.networkError",
            "Ndodhi një gabim. Ju lutem provoni përsëri më vonë."
          )
        );
        setPageState("invalid");
      }
    };

    fetchApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSuccess = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setPageState("success");
  }, []);

  useEffect(() => {
    if (pageState === "success" || pageState === "form") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [pageState]);

  useEffect(() => {
    if (pageState !== "success") return undefined;
    const timeoutId = window.setTimeout(() => {
      navigate("/");
    }, 4000);
    return () => window.clearTimeout(timeoutId);
  }, [pageState, navigate]);

  if (pageState === "loading") {
    return <Spinner />;
  }

  if (pageState === "invalid") {
    return (
      <div className={styles.StateContainer}>
        <Result
          status="error"
          title={errorTitle}
          subTitle={errorMessage}
          extra={
            <div className={coachFormStyles.ButtonAlignment}>
              <Button
                type="primary"
                className={coachFormStyles.ButtonStyle}
                onClick={() => navigate("/contact")}
              >
                {t("app.coachApplicationReview.contactUs", "Na kontaktoni")}
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  if (pageState === "success") {
    return (
      <div className={styles.StateContainer}>
        <Result
          status="success"
          title={t("app.coachApplicationReview.successTitle", "Aplikimi u përditësua me sukses!")}
          subTitle={
            <>
              {t("app.coachApplicationReview.successSubtitle")}
              <br />
              {t("app.coachApplicationReview.redirectHome")}
            </>
          }
        />
      </div>
    );
  }

  if (!application || !token || !personalInfo || !professionalInfo) {
    return <Spinner />;
  }

  return (
    <div className={`${coachesStyles.CoachesContainer} ${styles.ReviewPage}`}>
      <Modal
        open={noticesOpen}
        onCancel={() => setNoticesOpen(false)}
        footer={null}
        centered
        className={styles.NoticesModal}
        title={t("app.coachApplicationReview.title")}
      >
        <div className={styles.IntroBanner}>
          <p className={styles.IntroText}>
            {t("app.coachApplicationReview.intro")}
          </p>
        </div>

        {application.courseName && (
          <p className={styles.CourseName}>
            {t("app.coachApplicationReview.subtitle")}: {application.courseName}
          </p>
        )}

        {application.adminComment && (
          <div className={styles.AdminCommentBanner}>
            <p className={styles.AdminCommentLabel}>
              {t("app.coachApplicationReview.adminCommentLabel")}
            </p>
            <p className={styles.AdminCommentText}>{application.adminComment}</p>
          </div>
        )}

        <div className={`${coachFormStyles.ButtonAlignment} ${styles.NoticesModalActions}`}>
          <Button
            type="primary"
            className={coachFormStyles.ButtonStyle}
            onClick={() => setNoticesOpen(false)}
          >
            {t("app.coachApplicationReview.continueToUpdate")}
          </Button>
        </div>
      </Modal>

      <div className={styles.ReviewBody}>
        <img className={coachesStyles.CoachesPhoto} src={CoachesImage} alt="Coaches" />
        <div className={coachesStyles.CoachStepper}>
          <button
            type="button"
            className={styles.ViewInstructions}
            onClick={() => setNoticesOpen(true)}
          >
            {t("app.coachApplicationReview.viewInstructions")}
          </button>

          <Steps current={currentStep - 1} progressDot direction="horizontal" responsive={false}>
            <Step />
            <Step />
            <Step />
          </Steps>

          {currentStep === 1 && (
            <CoachStepTwo
              setCurrentStep={setCurrentStep}
              initialValues={personalInfo}
              persistToSession={false}
              onSave={setPersonalInfo}
              reviewLayout
            />
          )}
          {currentStep === 2 && (
            <CoachStepThree
              setCurrentStep={setCurrentStep}
              initialValues={professionalInfo}
              persistToSession={false}
              onSave={setProfessionalInfo}
              reviewLayout
            />
          )}
          {currentStep === 3 && (
            <CoachStepFour
              courseId={application.courseId}
              review={{
                token,
                application: toApplicationPayload(
                  application.courseId,
                  personalInfo,
                  professionalInfo
                ),
                documents: {
                  photoUrl: application.photoUrl,
                  medicalCertificateUrl: application.medicalCertificateUrl,
                  noConvictionProofUrl: application.noConvictionProofUrl,
                  oldLicenseUrl: application.oldLicenseUrl,
                  diplomaCopyUrl: application.diplomaCopyUrl,
                },
                onSuccess: handleSuccess,
                onBack: () => setCurrentStep(2),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachApplicationReview;
